import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import {
  useCreateLessonPlanHook,
  useUpdateLessonPlanHook,
} from "../../../hooks/useLessonPlanHook";
import { useClassHook, useSubjectsHook } from "../../../hooks/useAcademicHook";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";
import {
  lessonPlanStatusOptions,
  lessonPlanTermOptions,
} from "./lessonPlan.helpers";
import {
  TLessonPlan,
  TLessonPlanStatus,
} from "../../../apis/lessonPlan/lessonPlan.interfaces";

const lessonPlanSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(200, "Title must not exceed 200 characters"),
  description: z
    .string()
    .max(2000, "Description must not exceed 2000 characters")
    .optional()
    .or(z.literal("")),
  academicYear: z
    .string()
    .min(1, "Academic year is required")
    .max(20, "Academic year must not exceed 20 characters"),
  term: z.string().optional().or(z.literal("")),
  classId: z.string().optional().or(z.literal("")),
  /** Wire `courseId` — BE uses this for the institute subject id (see BE §2.7). */
  courseId: z.string().optional().or(z.literal("")),
  status: z.enum(["draft", "in_review", "approved", "active", "archived"]),
});

type LessonPlanFormData = z.infer<typeof lessonPlanSchema>;

interface ManageLessonPlanDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided, the dialog operates in edit mode (PATCH). Otherwise create (POST). */
  plan?: TLessonPlan | null;
}

const emptyDefaults: LessonPlanFormData = {
  title: "",
  description: "",
  academicYear: "",
  term: "",
  classId: "",
  courseId: "",
  status: "draft",
};

const planToFormDefaults = (plan: TLessonPlan): LessonPlanFormData => ({
  title: plan.title ?? "",
  description: plan.description ?? "",
  academicYear: plan.academicYear ?? "",
  term: plan.term ?? "",
  classId: plan.class?.id ?? "",
  courseId: plan.course?.id ?? "",
  status: (plan.status as TLessonPlanStatus) ?? "draft",
});

const ManageLessonPlanDialog: FC<ManageLessonPlanDialogProps> = ({
  open,
  onClose,
  plan,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createLessonPlanMutation = useCreateLessonPlanHook();
  const updateLessonPlanMutation = useUpdateLessonPlanHook();
  const { data: classes } = useClassHook();
  const { data: subjects } = useSubjectsHook();

  const isEditMode = !!plan;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LessonPlanFormData>({
    resolver: zodResolver(lessonPlanSchema),
    defaultValues: emptyDefaults,
  });

  useEffect(() => {
    if (open) {
      reset(plan ? planToFormDefaults(plan) : emptyDefaults);
    }
  }, [open, plan, reset]);

  const onSubmit = async (data: LessonPlanFormData) => {
    if (!selectedInstitute) {
      logger.show_error(
        "Please select an institute from the dashboard before saving a lesson plan.",
      );
      return;
    }

    const subjectMeta = data.courseId
      ? subjects?.find((s) => s.id === data.courseId)
      : undefined;
    const subjectLabel = subjectMeta?.name;

    try {
      if (isEditMode && plan) {
        await updateLessonPlanMutation.mutateAsync({
          id: plan.id,
          request: {
            title: data.title,
            description: data.description || undefined,
            academicYear: data.academicYear,
            term: data.term || undefined,
            classId: data.classId || undefined,
            courseId: data.courseId || undefined,
            subject: subjectLabel || undefined,
            status: data.status as TLessonPlanStatus,
          },
        });
        logger.show_success("Lesson plan updated successfully!");
      } else {
        await createLessonPlanMutation.mutateAsync({
          instituteId: selectedInstitute.id,
          title: data.title,
          description: data.description || undefined,
          academicYear: data.academicYear,
          term: data.term || undefined,
          classId: data.classId || undefined,
          courseId: data.courseId || undefined,
          subject: subjectLabel || undefined,
          status: data.status as TLessonPlanStatus,
        });
        logger.show_success("Lesson plan created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} lesson plan. Please try again.`,
      );
    }
  };

  const termOptions = [
    { key: "", value: "—" },
    ...lessonPlanTermOptions.map((t) => ({
      key: t,
      value: t.charAt(0).toUpperCase() + t.slice(1),
    })),
  ];

  const statusOptions = lessonPlanStatusOptions.map((s) => ({
    key: s,
    value:
      s === "in_review"
        ? "In Review"
        : s.charAt(0).toUpperCase() + s.slice(1),
  }));

  const classOptions = [
    { key: "", value: "—" },
    ...(classes?.map((c) => ({ key: c.id, value: c.name })) ?? []),
  ];

  const subjectSelectOptions = [
    { key: "", value: "—" },
    ...(subjects?.map((s) => ({ key: s.id, value: s.name })) ?? []),
  ];

  const activeMutation = isEditMode
    ? updateLessonPlanMutation
    : createLessonPlanMutation;
  const isLoading = isSubmitting || activeMutation.isPending;
  const isError = activeMutation.isError;
  const errorMessage = activeMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-lesson-plan-dialog"
    >
      <DialogTitle data-testid="manage-lesson-plan-dialog-title">
        {isEditMode ? "Edit Lesson Plan" : "Add New Lesson Plan"}
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-testid="lesson-plan-form"
      >
        <DialogContent data-testid="lesson-plan-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="lesson-plan-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} lesson plan. Please try again.`}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <ControlledTextField
              control={control}
              name="title"
              id="lesson-plan-title"
              label="Title"
              required
              errorMessage={errors.title?.message}
              inputProps={{ "data-testid": "lesson-plan-title-input" }}
            />
            <ControlledTextField
              control={control}
              name="academicYear"
              id="lesson-plan-academic-year"
              label="Academic Year"
              required
              errorMessage={errors.academicYear?.message}
              inputProps={{
                "data-testid": "lesson-plan-academic-year-input",
                placeholder: "2025-2026",
              }}
            />
            <ControlledSelectField
              control={control}
              name="term"
              id="lesson-plan-term"
              label="Term"
              itemList={termOptions}
              errorMessage={errors.term?.message}
            />
            <ControlledSelectField
              control={control}
              name="status"
              id="lesson-plan-status"
              label="Status"
              itemList={statusOptions}
              isRequired
              errorMessage={errors.status?.message}
            />
            <ControlledSelectField
              control={control}
              name="classId"
              id="lesson-plan-class"
              label="Class"
              itemList={classOptions}
              errorMessage={errors.classId?.message}
            />
            <ControlledSelectField
              control={control}
              name="courseId"
              id="lesson-plan-subject-catalog"
              label="Subject"
              itemList={subjectSelectOptions}
              errorMessage={errors.courseId?.message}
            />
            <ControlledTextField
              control={control}
              name="description"
              id="lesson-plan-description"
              label="Description"
              multiline
              rows={3}
              errorMessage={errors.description?.message}
              inputProps={{ "data-testid": "lesson-plan-description-input" }}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-lesson-plan-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-lesson-plan-button"
          >
            {isLoading
              ? isEditMode
                ? "Saving..."
                : "Creating..."
              : isEditMode
                ? "Save Changes"
                : "Create Lesson Plan"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageLessonPlanDialog;
